import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fmtDate } from "../lib/dateFormat";
import { istToday } from "@/lib/dateFormat";

export default function SSResetStockPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r));

  const [ssList, setSsList] = useState([]);
  const [selectedSS, setSelectedSS] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(istToday());
  const [stockData, setStockData] = useState([]);
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [resets, setResets] = useState([]);
  const [pendingResets, setPendingResets] = useState([]);

  useEffect(() => {
    api.get("/ss-inventory/ss-list").then(r => setSsList(r.data)).catch(() => {});
    api.get("/ss-inventory/resets").then(r => setResets(r.data)).catch(() => {});
    if (isAdmin) api.get("/ss-inventory/resets", { params: { status: "Pending Admin Approval" } }).then(r => setPendingResets(r.data)).catch(() => {});
  }, []);

  const loadStock = async (ssId) => {
    setSelectedSS(ssId);
    if (!ssId) return;
    try {
      const { data } = await api.get(`/ss-inventory/stock/${ssId}`);
      setStockData(data);
      // Build per-batch lines from ledger data
      const batchLines = [];
      for (const item of data) {
        // Fetch ledger for this SKU to get per-batch breakdown
        try {
          const { data: ledger } = await api.get(`/ss-inventory/stock/${ssId}/ledger/${item.sku_id}`);
          const batchMap = {};
          for (const e of ledger) {
            const bn = (e.batch_number || "").toUpperCase().replace(/[^A-Z0-9]/g, "") || "(no batch)";
            if (!batchMap[bn]) batchMap[bn] = { qty: 0 };
            const q = Math.abs(e.quantity || 0);
            if (e.entry_type === "stock_in" || ["IN", "OPENING", "ADJUSTMENT", "RESET"].includes(e.type) || (e.entry_type === "" && e.quantity > 0)) {
              batchMap[bn].qty += q;
            } else {
              batchMap[bn].qty -= q;
            }
          }
          for (const [bn, v] of Object.entries(batchMap)) {
            batchLines.push({ sku_id: item.sku_id, sku_name: item.sku_name, batch_number: bn === "(no batch)" ? "" : bn, current_balance: v.qty, counted_qty: "" });
          }
        } catch { /* ledger fetch fail — show SKU-level only */
          batchLines.push({ sku_id: item.sku_id, sku_name: item.sku_name, batch_number: "", current_balance: item.current_balance, counted_qty: "" });
        }
      }
      setLines(batchLines);
    } catch (e) { toast.error("Failed to load stock"); }
  };

  const updateLine = (idx, field, value) => {
    const updated = [...lines];
    updated[idx] = { ...updated[idx], [field]: value };
    setLines(updated);
  };

  const handleSubmit = async () => {
    const toSubmit = lines.filter(l => l.counted_qty !== "" && l.counted_qty !== null && l.counted_qty !== undefined);
    if (toSubmit.length === 0) { toast.error("Enter at least one counted quantity"); return; }
    setSubmitting(true);
    try {
      await api.post("/ss-inventory/reset/submit", {
        ss_customer_id: selectedSS, effective_date: effectiveDate,
        lines: toSubmit.map(l => ({ sku_id: l.sku_id, sku_name: l.sku_name, batch_number: l.batch_number, counted_qty: parseInt(l.counted_qty) || 0 })),
      });
      toast.success("Reset submitted for Admin approval");
      setLines(lines.map(l => ({ ...l, counted_qty: "" })));
      api.get("/ss-inventory/resets").then(r => setResets(r.data)).catch(() => {});
      if (isAdmin) api.get("/ss-inventory/resets", { params: { status: "Pending Admin Approval" } }).then(r => setPendingResets(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSubmitting(false);
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/ss-inventory/reset/${id}/approve`);
      toast.success("Reset approved — ledger updated");
      setPendingResets(p => p.filter(r => r._id !== id));
      api.get("/ss-inventory/resets").then(r => setResets(r.data)).catch(() => {});
      if (selectedSS) loadStock(selectedSS);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleReject = async (id) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    try {
      await api.put(`/ss-inventory/reset/${id}/reject`, { reason });
      toast.success("Reset rejected");
      setPendingResets(p => p.filter(r => r._id !== id));
      api.get("/ss-inventory/resets").then(r => setResets(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="ss-reset-stock-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/ss-inventory")}><ArrowLeft size={16} /></Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">Reset & Opening Stock</h1>
          <p className="text-sm text-zinc-500">Set the true physical stock per SKU per batch</p>
        </div>
      </div>

      {/* Pending Resets (Admin) */}
      {isAdmin && pendingResets.length > 0 && (
        <div className="mb-6 border border-amber-200 rounded-sm p-4 bg-amber-50" data-testid="pending-resets">
          <h2 className="text-sm font-semibold text-amber-800 mb-3">Pending Reset Approvals ({pendingResets.length})</h2>
          {pendingResets.map(r => (
            <div key={r._id} className="border border-amber-300 bg-white rounded-sm p-3 mb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold">{r.ss_name}</span>
                <span className="text-[10px] text-zinc-400">by {r.submitted_by_name} · {fmtDate(r.submitted_at)}</span>
              </div>
              <table className="w-full text-xs mb-2"><thead><tr className="bg-zinc-50"><th className="px-2 py-1 text-left text-[10px]">SKU</th><th className="px-2 py-1 text-left text-[10px]">Batch</th><th className="px-2 py-1 text-right text-[10px]">System</th><th className="px-2 py-1 text-right text-[10px]">Counted</th><th className="px-2 py-1 text-right text-[10px]">Delta</th></tr></thead><tbody>
                {(r.lines || []).map((l, i) => <tr key={i} className="border-t border-zinc-100"><td className="px-2 py-1">{l.sku_name}</td><td className="px-2 py-1 font-mono">{l.batch_number || "—"}</td><td className="px-2 py-1 text-right font-mono">{l.current_balance}</td><td className="px-2 py-1 text-right font-mono font-semibold">{l.counted_qty}</td><td className={`px-2 py-1 text-right font-mono font-semibold ${l.delta > 0 ? "text-green-600" : l.delta < 0 ? "text-red-600" : ""}`}>{l.delta > 0 ? "+" : ""}{l.delta}</td></tr>)}
              </tbody></table>
              <div className="flex gap-2">
                <Button size="sm" className="h-6 text-[10px] rounded-sm" onClick={() => handleApprove(r._id)} data-testid={`approve-reset-${r._id}`}><Check size={12} className="mr-1" />Approve</Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" onClick={() => handleReject(r._id)} data-testid={`reject-reset-${r._id}`}><X size={12} className="mr-1" />Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit Form */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div>
          <Label className="text-xs">Super Stockist</Label>
          <Select value={selectedSS || "none"} onValueChange={v => loadStock(v === "none" ? "" : v)}>
            <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select SS" /></SelectTrigger>
            <SelectContent><SelectItem value="none">-- Select --</SelectItem>{ssList.map(s => <SelectItem key={s._id} value={s._id}>{s.company_name || s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Effective Date</Label>
          <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} className="mt-1 rounded-sm" />
        </div>
      </div>

      {selectedSS && lines.length > 0 && (
        <div className="border border-zinc-200 rounded-sm overflow-x-auto mb-6" data-testid="reset-lines-table">
          <table className="data-table w-full">
            <thead><tr><th>SKU</th><th>Batch</th><th className="text-right">System Balance</th><th className="text-right">Counted Qty</th><th className="text-right">Delta</th></tr></thead>
            <tbody>
              {lines.map((l, i) => {
                const counted = parseInt(l.counted_qty);
                const delta = !isNaN(counted) ? counted - l.current_balance : null;
                return (
                  <tr key={i}>
                    <td className="text-xs">{l.sku_name}</td>
                    <td className="text-xs font-mono">{l.batch_number || "—"}</td>
                    <td className="text-right font-mono text-xs">{l.current_balance}</td>
                    <td className="text-right"><Input type="number" value={l.counted_qty} onChange={e => updateLine(i, "counted_qty", e.target.value)} className="w-24 h-7 text-xs font-mono text-right rounded-sm ml-auto" placeholder="—" /></td>
                    <td className={`text-right font-mono text-xs font-semibold ${delta !== null ? (delta > 0 ? "text-green-600" : delta < 0 ? "text-red-600" : "") : "text-zinc-300"}`}>{delta !== null ? (delta > 0 ? `+${delta}` : delta) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-zinc-200">
            <Button onClick={handleSubmit} disabled={submitting} className="rounded-sm text-xs" data-testid="submit-reset-btn"><RotateCcw size={14} className="mr-1" />{submitting ? "Submitting..." : "Submit for Approval"}</Button>
          </div>
        </div>
      )}

      {/* Reset History */}
      {resets.length > 0 && (
        <div className="border border-zinc-200 rounded-sm" data-testid="reset-history">
          <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200"><h2 className="text-sm font-semibold text-zinc-700">Reset History ({resets.length})</h2></div>
          <table className="data-table w-full"><thead><tr><th>SS</th><th>Date</th><th>Lines</th><th>Status</th><th>Submitted By</th><th>Submitted At</th></tr></thead><tbody>
            {resets.map(r => {
              const sc = r.status === "Approved" ? "bg-green-100 text-green-700" : r.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700";
              return <tr key={r._id}><td className="text-xs">{r.ss_name}</td><td className="text-xs">{fmtDate(r.effective_date)}</td><td className="text-xs font-mono">{(r.lines || []).length}</td><td><span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${sc}`}>{r.status}</span></td><td className="text-[10px] text-zinc-500">{r.submitted_by_name}</td><td className="text-[10px] text-zinc-500">{fmtDate(r.submitted_at)}</td></tr>;
            })}
          </tbody></table>
        </div>
      )}
    </div>
  );
}
